import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'src/context/AuthContext';
import { sessionsService, SessionData } from 'src/services/sessionsService';

// Query keys for react-query
export const TUTOR_SESSIONS_QUERY_KEYS = {
  all: ['tutor-sessions'] as const,
  lists: () => [...TUTOR_SESSIONS_QUERY_KEYS.all, 'list'] as const,
  upcoming: (tutorId: string) => [...TUTOR_SESSIONS_QUERY_KEYS.lists(), 'upcoming', tutorId] as const,
  past: (tutorId: string) => [...TUTOR_SESSIONS_QUERY_KEYS.lists(), 'past', tutorId] as const,
  today: (tutorId: string, date: string) => [...TUTOR_SESSIONS_QUERY_KEYS.lists(), 'today', tutorId, date] as const,
  all_sessions: (tutorId: string) => [...TUTOR_SESSIONS_QUERY_KEYS.lists(), 'all', tutorId] as const,
};

/**
 * Hook to fetch upcoming sessions with React Query
 * Uses 10 minutes stale time to avoid redundant fetches
 */
export const useTutorUpcomingSessions = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: TUTOR_SESSIONS_QUERY_KEYS.upcoming(user?.id || ''),
    queryFn: () => {
      if (!user?.id) throw new Error('User not authenticated');
      return sessionsService.fetchTutorUpcomingSessions(user.id);
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook to fetch past sessions with React Query
 * Uses 10 minutes stale time to avoid redundant fetches
 */
export const useTutorPastSessions = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: TUTOR_SESSIONS_QUERY_KEYS.past(user?.id || ''),
    queryFn: () => {
      if (!user?.id) throw new Error('User not authenticated');
      return sessionsService.fetchTutorPastSessions(user.id);
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook to fetch today's sessions with React Query
 * Uses 10 minutes stale time to avoid redundant fetches
 */
export const useTutorTodaySessions = (selectedDate: Date) => {
  const { user } = useAuth();
  const dateString = selectedDate.toISOString().split('T')[0]; // YYYY-MM-DD format
  
  return useQuery({
    queryKey: TUTOR_SESSIONS_QUERY_KEYS.today(user?.id || '', dateString),
    queryFn: () => {
      if (!user?.id) throw new Error('User not authenticated');
      return sessionsService.fetchTutorSessionsForDate(user.id, selectedDate);
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook to fetch all tutor sessions with React Query
 * Uses 10 minutes stale time to avoid redundant fetches
 */
export const useTutorAllSessions = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: TUTOR_SESSIONS_QUERY_KEYS.all_sessions(user?.id || ''),
    queryFn: () => {
      if (!user?.id) throw new Error('User not authenticated');
      return sessionsService.fetchTutorSessions(user.id);
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Combined hook that provides all session data
 * This ensures data is fetched once and shared across components
 */
export const useTutorSessionsData = (selectedDate?: Date) => {
  const upcomingQuery = useTutorUpcomingSessions();
  const pastQuery = useTutorPastSessions();
  const todayQuery = useTutorTodaySessions(selectedDate || new Date());
  
  return {
    // Upcoming sessions
    upcomingSessions: upcomingQuery.data || [],
    isLoadingUpcoming: upcomingQuery.isLoading,
    upcomingError: upcomingQuery.error?.message || null,
    refetchUpcoming: upcomingQuery.refetch,
    
    // Past sessions
    pastSessions: pastQuery.data || [],
    isLoadingPast: pastQuery.isLoading,
    pastError: pastQuery.error?.message || null,
    refetchPast: pastQuery.refetch,
    
    // Today's sessions
    todaySessions: todayQuery.data || [],
    isLoadingToday: todayQuery.isLoading,
    todayError: todayQuery.error?.message || null,
    refetchToday: todayQuery.refetch,
    
    // Combined states
    isLoading: upcomingQuery.isLoading || pastQuery.isLoading || todayQuery.isLoading,
    hasError: !!(upcomingQuery.error || pastQuery.error || todayQuery.error),
    error: upcomingQuery.error?.message || pastQuery.error?.message || todayQuery.error?.message || null,
    
    // Refresh all
    refreshAll: () => {
      upcomingQuery.refetch();
      pastQuery.refetch();
      todayQuery.refetch();
    }
  };
};